import { DatesRangeValue } from "@mantine/dates"
import dayjs from "dayjs"
import { useCallback, useState } from "react"

import { type DateRangeValue } from "../DatePickerFilter"
import { DateFilterDto, DateFilterOperator } from "../date-filter.types"

import { useComponentOperatorMapping } from "./useComponentOperatorMapping"

export type DateFilterValueComponentFactory = {
  getComponentForOperator: (operator: DateFilterOperator) => React.ReactNode
  dateRange: DatesRangeValue<Date>
  setDateRange: (dateRange: DatesRangeValue<Date>) => void
}

export type DateFilterValueComponentFactoryProps = {
  value: DateFilterDto | null
  locale?: string
}

export const useDateFilterValueComponentFactory = ({
  value,
  locale = "fr",
}: DateFilterValueComponentFactoryProps): DateFilterValueComponentFactory => {
  const [dateRange, setDateRange] = useState<DateRangeValue>([
    value?.startDate ?? dayjs().subtract(1, "day").toDate(),
    value?.endDate ?? dayjs().toDate(),
  ])

  const handleChange = useCallback((range: DateRangeValue) => {
    const [startDate, endDate] = range
    setDateRange([
      startDate
        ? dayjs(startDate).startOf("day").add(1, "hour").toDate()
        : null,
      endDate ? dayjs(endDate).endOf("day").subtract(1, "hour").toDate() : null,
    ])
  }, [])

  const { getComponentForOperator } = useComponentOperatorMapping({
    locale,
    value: dateRange,
    onChange: handleChange,
  })

  return {
    getComponentForOperator,
    dateRange,
    setDateRange,
  } satisfies DateFilterValueComponentFactory
}
